import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import moment from 'moment';
import t from 'react-translate';

import { AngularContext } from 'react-app';
import { config } from '@config/pendo.config.json';
import PusherService from 'shared/services/pusher-service';
import { useAppDispatch } from 'redux/store';
import { requestFeedback } from 'redux/actions/submissions';
import {
  AIEvaluationStatus,
  AIFeedbackDataRequest,
} from 'redux/schemas/models/submissions';
import { InsightVisibility } from 'redux/schemas/models/exercise';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { getCurrentUser } from 'redux/selectors/users';
import { halfSpacing, largeSpacing, standardSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';

import Hero from './hero';
import Section from './section';
import DraftFeedback from './draft-feedback';
import BottomNote from './bottom-note';
import VisibilityBadge from './visibility-badge';
import ReviseLink from './revise-link';
import GeneratingState from './generating-state';
import ErrorState from './error-state';
import getErrorMessage, { isInternalErrorCode } from './get-error-message';

const AI_FEEDBACK_EVENT = 'ai_feedback_received';
const AI_FEEDBACK_REQUESTED_EVENT = 'ai_feedback_requested';

const panelStyles = css`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding-bottom: ${tripleSpacing}px;
`;

const sectionsStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${standardSpacing}px;
  padding: ${halfSpacing}px ${largeSpacing}px;
`;

const AiInsightsPanel: React.FC = () => {
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState<boolean>(false);
  const [hasPermissionError, setHasPermissionError] = useState<boolean>(false);
  const { injectServices } = React.useContext(AngularContext);
  const [ReportsManager, $state] = injectServices(['ReportsManager', '$state']);

  const dispatch = useAppDispatch();
  const currentUser = useSelector(getCurrentUser);
  const currentCourse = useSelector(getCurrentCourse);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, currentCourse?.catalogId));
  const { currentReport } = ReportsManager;
  const courseId = currentCourse?.id;
  const reportAiFeedback = currentReport?.aiFeedback;
  const feedbackDataRequest = currentReport?.feedbackDataRequest as AIFeedbackDataRequest;

  const responseCode = feedbackDataRequest?.feedbackProcessingStatus?.responseCode;
  const statusErrorMessage = feedbackDataRequest?.feedbackProcessingStatus?.errorMessage;
  const hasStatusError = feedbackDataRequest?.feedbackProcessingStatus?.status === 'error';
  const hasError = isInternalErrorCode(responseCode) || hasStatusError;
  const hasFeedbackDataRequest = !!feedbackDataRequest;
  const isGenerating = feedbackDataRequest?.completed === false || isGeneratingFeedback;
  const isLearnerView = currentReport?.exercise?.insightVisibility === InsightVisibility.LEARNERS;

  const canRevise = useMemo(() => {
    if (!currentReport) return false;
    const isTeamSubmission = currentReport?.ownerType === 'Team'
      && currentReport?.exercise?.collaborators?.some(c => c.id === currentUser?.id);
    const isSubmissionOwner = currentReport?.owner?.id === currentUser?.id;
    if (!isSubmissionOwner && !isTeamSubmission) return false;
    if (reportAiFeedback?.evaluation !== AIEvaluationStatus.NEEDS_IMPROVEMENT) return false;
    if (currentReport?.exercise?.hardDeadline && moment().isAfter(currentReport?.exercise?.deadline)) return false;
    if (currentReport?.exercise?.numberSub > 1 || currentReport?.exercise?.submissions?.length > 1) return false;
    return true;
  }, [currentUser?.id, currentReport, reportAiFeedback?.evaluation]);

  const handleRevise = useCallback(() => {
    $state.go('lecture-page', {
      catalogId: currentCourse.catalogId,
      id: currentReport?.exercise?.lecturePageId,
      lectureActivityId: currentReport?.exercise?.lectureComponentId,
      reportId: currentReport?.id,
      revise: true,
    }, { reload: true });
  }, [
    $state,
    currentCourse.catalogId,
    currentReport?.exercise?.lecturePageId,
    currentReport?.exercise?.lectureComponentId,
    currentReport?.id,
  ]);

  const fetchByReportFeedback = useCallback(async () => {
    if (!currentReport?.id) return;
    await dispatch(
      requestFeedback({
        catalogId: currentCourse.catalogId,
        reportId: currentReport.id,
      }),
    )
      .unwrap()
      .then(() => {
        setHasPermissionError(false);
        setIsGeneratingFeedback(true);
      })
      .catch(() => {
        setHasPermissionError(true);
      });
  }, [currentCourse.catalogId, currentReport?.id, dispatch]);

  useEffect(() => {
    if (!hasFeedbackDataRequest || isInternalErrorCode(responseCode)) {
      fetchByReportFeedback();
    } else {
      setHasPermissionError(false);
    }
  }, [fetchByReportFeedback, hasFeedbackDataRequest, responseCode]);

  useEffect(() => {
    if (!courseId) return undefined;
    const courseChannel = PusherService.initializeCourseChannel(courseId);
    const handleAiFeedbackUpdate = async () => {
      setIsGeneratingFeedback(true);
      await ReportsManager.getSingleSubmission(currentCourse.catalogId, currentReport?.id).finally(() => {
        setIsGeneratingFeedback(false);
      });
    };
    if (courseChannel) {
      courseChannel.bind(AI_FEEDBACK_EVENT, handleAiFeedbackUpdate);
      courseChannel.bind(AI_FEEDBACK_REQUESTED_EVENT, handleAiFeedbackUpdate);
    }
    return () => {
      if (courseChannel) {
        courseChannel.unbind(AI_FEEDBACK_EVENT, handleAiFeedbackUpdate);
        courseChannel.unbind(AI_FEEDBACK_REQUESTED_EVENT, handleAiFeedbackUpdate);
      }
    };
  }, [courseId, currentCourse?.catalogId, currentReport?.id, ReportsManager]);

  if (hasPermissionError || (!feedbackDataRequest && !isGenerating)) {
    return null;
  }

  const evaluation = reportAiFeedback?.evaluation;
  const hasSkippedFiles = (feedbackDataRequest?.feedbackProcessingStatus?.submissionSkippedFiles?.length ?? 0) > 0;
  const hasExternalLink = feedbackDataRequest?.feedbackProcessingStatus?.hasExternalLink;
  const bottomNoteMessage = (hasSkippedFiles || hasExternalLink)
    ? feedbackDataRequest?.feedbackProcessingStatus?.fullErrorMessage
    : undefined;
  const workingWell = reportAiFeedback?.workingWell;
  const needsImprovement = reportAiFeedback?.needsImprovement;

  let body: React.ReactNode = null;
  if (isGenerating) {
    body = <GeneratingState />;
  } else if (hasError) {
    body = <ErrorState message={getErrorMessage({ responseCode, statusErrorMessage, isLearnerView })} />;
  } else if (evaluation) {
    body = (
      <>
        <Hero evaluation={evaluation} reason={reportAiFeedback?.reason} />
        <div css={sectionsStyles}>
          {isLearnerView ? (
            <>
              <Section kind='did-well' content={workingWell} />
              <Section kind='can-grow' content={needsImprovement} />
            </>
          ) : (
            <>
              <Section kind='worked' content={workingWell} />
              <Section kind='improved' content={needsImprovement} />
              <DraftFeedback feedback={reportAiFeedback?.feedback} isGenerating={isGenerating} />
            </>
          )}
          <BottomNote message={bottomNoteMessage} />
        </div>
        {isLearnerView && canRevise && <ReviseLink onRevise={handleRevise} />}
        <VisibilityBadge
          variant={isLearnerView ? 'learner' : 'admin-only'}
          courseAliases={courseAliases}
        />
      </>
    );
  }

  return (
    <aside
      css={panelStyles}
      aria-label={t.SUBMISSIONS.AI_ASSIGNMENT.TITLE_INSIGHT()}
      data-qa={config.pendo.submissionView.aiInsights.panel}
    >
      {body}
    </aside>
  );
};

export default AiInsightsPanel;
